import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { unwrapResult } from "@reduxjs/toolkit";
import dayjs, { Dayjs } from "dayjs";
import { SelectChangeEvent } from "@mui/material";

import { dateNormalizer, errorNormalizer } from "../../../shared/Helpers/functions";
import {
  CustomAlert,
  CustomSelect,
  Picker,
  CustomButton,
  Text,
} from "../../../shared/uiComponents";
import { SelectDataProperties } from "../../../shared/uiComponents/Dropdown";
import { useCompareDates } from "../../../shared/Helpers/hooks";
import { InfoRow } from "../../../components/StyledComponents";
import Delete from "../../../components/Delete";
import { TimeInputsWrapper } from "../../Reports/helpers";
import { months } from "../Helpers/constants";
import { DayProperties } from "../Helpers/interfaces";
import { useResetCalendarData } from "../Helpers/hooks";

import { DispatchProperties, useSelector } from "../../../redux/store";
import {
  adminCreateEvent,
  setClient,
  setTherapist,
  setAdminAddEvent,
  clearFilterData,
} from "../../../redux/State/clientSlice/calendarSlice";
import { AdminCreateEventProperties } from "../../../redux/API/ClientAPIHelpers/calendarProperties";
import { getTherapists, getUserClients } from "../../../redux/State/clientSlice/userClientSlice";
import { getSessionTypes } from "../../../redux/State/clientSlice/sessionSlice";

const initialExpirationDate = dayjs().add(2, "days");

const AdminAddEvent = () => {
  const dispatch = useDispatch<DispatchProperties>();

  const open = useSelector((state) => state.calendar.popups.addAdmin);
  const selectedDay = useSelector((state) => state.calendar.selectedDay);

  const closeHandler = () => {
    dispatch(setAdminAddEvent(false));
    dispatch(clearFilterData());
  };

  return (
    <CustomAlert
      open={open}
      onClose={closeHandler}
      title={`Add calendar event to ${months[selectedDay.month]} ${selectedDay.day} ${
        selectedDay.year
      }`}
      Content={() => <AddContent selectedDay={selectedDay} onClose={closeHandler} />}
    />
  );
};

const AddContent = ({
  selectedDay,
  onClose,
}: {
  selectedDay: DayProperties;
  onClose: () => void;
}) => {
  const dispatch = useDispatch<DispatchProperties>();
  const { resetData } = useResetCalendarData();

  const [loading, setLoading] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(8);
  const [sessionTypeId, setSessionTypeId] = useState<string>("0");
  const [clientId, setClientId] = useState<string>("0");
  const [userId, setUserId] = useState<string>("0");
  const [clientList, setClientList] = useState<Array<SelectDataProperties>>([]);
  const [userList, setUserList] = useState<Array<SelectDataProperties>>([]);
  const [startTimeValue, setStartTimeValue] = useState<Dayjs | null>(null);
  const [endTimeValue, setEndTimeValue] = useState<Dayjs | null>(null);
  const [expirationDateValue, setExpirationDateValue] = useState<Dayjs | null>(
    initialExpirationDate
  );

  const sessionTypes = useSelector((state) => state.session.sessionTypes);
  const userClients = useSelector((state) => state.userClient.userClients);
  const days = useSelector((state) => state.calendar.displayedCalendarPageDays);
  const filterData = useSelector((state) => state.calendar.filterData);
  const usersData = useSelector((state) => state.user.users);
  const clientUsers = useSelector((state) => state.userClient.therapists);

  const { hasError, message } = useCompareDates(startTimeValue, endTimeValue);

  useEffect(() => {
    const { day, month, year } = selectedDay;
    const dateValue = new Date(year, month, day);
    const clientId = filterData.client?.id;
    const userId = filterData.therapist?.id;
    if (!clientId || !userId) return;

    dispatch(getSessionTypes({ clientId, date: dateNormalizer(dayjs(dateValue)), userId }));
  }, [dispatch, filterData.client?.id, filterData.therapist?.id, selectedDay]);

  useEffect(() => {
    const userId = filterData.therapist?.id;
    if (!userId) return;
    dispatch(
      getUserClients({
        userId,
        page: "1",
        pageSize: pageSize.toString(),
      })
    )
      .then(unwrapResult)
      .then((response) => {
        if (!response.query) return;

        const clientList = response.query.map((client) => ({
          id: client.id,
          name: client.fullName,
        }));
        if (!clientList.length) {
          setClientId("");
          return;
        }
        setClientList(clientList);
      })
      .catch(errorNormalizer);
  }, [filterData, pageSize, dispatch]);

  useEffect(() => {
    const clientId = filterData.client?.id;
    if (!clientId) return;
    dispatch(getTherapists({ clientId }))
      .then(unwrapResult)
      .then((response) => {
        if (!response) return;
        const data = response.map((user) => ({
          id: user.id,
          label: user.fullName,
        }));
        setUserList(data);
      })
      .catch(errorNormalizer);
  }, [filterData, dispatch]);

  const onClientChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setClientId(value);
    if (!userClients.query) return;
    const client = userClients.query.find((x) => x.id === value);
    dispatch(setClient(client));
  };

  const onUserChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setUserId(value);
    const user = usersData.query?.find((x) => x.id === value);
    if (!!user) dispatch(setTherapist(user));
    const therapist = clientUsers?.find((x) => x.id === value);
    if (!!therapist) dispatch(setTherapist(therapist));
  };

  const onTypeChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setSessionTypeId(value);
  };

  useEffect(() => {
    if (!!startTimeValue) {
      setEndTimeValue(startTimeValue.add(1, "h"));
    }
  }, [startTimeValue]);

  const addSession = () => {
    const { day, month, year } = selectedDay;
    if (!startTimeValue || !endTimeValue || !days.length) return;
    const hour = startTimeValue.hour();
    const minute = startTimeValue.minute();
    const dateValue = new Date(year, month, day, hour, minute);
    const startTime = dayjs(dateValue).format("YYYY-MM-DDTHH:mm");

    const endHour = endTimeValue.hour();
    const endMinute = endTimeValue.minute();
    const endTime = dayjs(new Date(year, month, day, endHour, endMinute)).format(
      "YYYY-MM-DDTHH:mm"
    );

    if (!startTime || !endTime || !userId) return;
    setLoading(true);

    const data: AdminCreateEventProperties = {
      userId: !!filterData.therapist?.id ? filterData.therapist?.id : userId,
      clientId: !!filterData.client?.id ? filterData.client?.id : clientId,
      date: dateNormalizer(dayjs(dateValue)),
      startTime,
      endTime,
      sessionTypeId: parseInt(sessionTypeId),
      expirationDate: dateNormalizer(dayjs(expirationDateValue)),
    };

    dispatch(adminCreateEvent(data))
      .then(unwrapResult)
      .then(() => {
        resetData();
        toast("Created");
        dispatch(setAdminAddEvent(false));
      })
      .catch(errorNormalizer)
      .finally(() => setLoading(false));
  };

  const removeProviderHandler = () => {
    dispatch(setTherapist(null));
  };

  const removeClientHandler = () => {
    dispatch(setClient(null));
    if (!filterData.therapist) onClose();
  };

  return (
    <>
      {filterData.therapist?.fullName && (
        <InfoRow style={{ marginBottom: "8px" }}>
          <Text title={"Provider Name:"} size="smallBold" />
          <Text title={filterData.therapist?.fullName} />
          <div style={{ width: "80px" }}>
            {!!filterData.client && !!userId ? (
              <Delete deleteHandler={removeProviderHandler} />
            ) : (
              <Delete deleteHandler={onClose} />
            )}
          </div>
        </InfoRow>
      )}
      {!!filterData.client?.id && !filterData.therapist?.id && (
        <CustomSelect
          label="Provider"
          data={userList}
          value={userId}
          setValue={onUserChange}
          className={"marginBottom16"}
        />
      )}
      {filterData.client?.fullName && (
        <InfoRow style={{ marginBottom: "8px" }}>
          <Text title={"Client Name:"} size="smallBold" />
          <Text title={filterData.client?.fullName} />
          <div style={{ width: "80px" }}>
            {!userId && <Delete deleteHandler={removeClientHandler} />}
          </div>
        </InfoRow>
      )}
      {!!filterData.therapist?.id && !filterData.client?.id && (
        <CustomSelect
          label="Client"
          data={clientList}
          value={clientId}
          setValue={onClientChange}
          className={"marginBottom16"}
          loadMore={{
            activate: !!userClients.totalNumberOfItems ? userClients.totalNumberOfItems > 8 : false,
            setSize: setPageSize,
          }}
        />
      )}
      {!!sessionTypes && (
        <CustomSelect
          label="Session type"
          data={[
            ...sessionTypes.filter((x) => {
              if (x.id === 0) return x;
              if (!filterData.therapist) return x;
              return x.allowedRoleSections?.find(
                (z) => z.id === filterData.therapist?.role.section.id
              );
            }),
          ]}
          value={sessionTypeId}
          setValue={onTypeChange}
          disabled={!filterData?.client?.id}
          className={"marginBottom16"}
        />
      )}
      <TimeInputsWrapper className="marginBottom16">
        <Picker.CustomTime label="Start time" value={startTimeValue} onChange={setStartTimeValue} />
        <Picker.CustomTime
          label="End time"
          value={endTimeValue}
          onChange={setEndTimeValue}
          error={hasError}
          errorMessage={message}
        />
      </TimeInputsWrapper>
      <TimeInputsWrapper className="marginBottom16">
        <Picker.CustomDate
          label="Expiration date"
          value={expirationDateValue}
          onChange={setExpirationDateValue}
        />
      </TimeInputsWrapper>
      <CustomButton
        onClick={addSession}
        title={"Add calendar event"}
        loading={loading}
        disabled={
          !startTimeValue ||
          !endTimeValue ||
          (!clientId && !filterData.client?.id) ||
          (!userId && !filterData.therapist?.id) ||
          hasError ||
          sessionTypeId === "0"
        }
      />
    </>
  );
};

export default AdminAddEvent;
